/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IActionContext } from '@msdyn365-commerce/core-internal';
import { CartLine, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { getByIdAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { IProductInfo } from '@msdyn365-commerce/telemetry-internal';

/**
 * Converts cart line products into promise of array of product info
 * @param cartLines Cart Lines with product info to collect
 * @param actionContext Action context
 * @returns Array of product info
 */
export const getProductInfoFromCartLinesAsync = async (cartLines: CartLine[], actionContext: IActionContext): Promise<IProductInfo[]> => {
    if (actionContext.requestContext.channel === undefined) {
        throw new Error('Unable to make product call, channel ID not provided');
    }

    const productPromises: Promise<IProductInfo>[] = [];

    // Call GetById API for all products in cartLines
    for (let i = 0; i < cartLines.length; i++) {
        const cartLine: CartLine = cartLines[i];

        if (cartLine.ProductId) {
            // The product id in cartLine actually contains recordId and not master product id so we can use it up here to lookup product.
            productPromises.push(
                getByIdAsync({ callerContext: actionContext }, cartLine.ProductId, actionContext.requestContext.channel.RecordId).then(
                    result => {
                        // Construct basic IProductInfo object for product
                        const productInfo = getProductInfoFromSimpleProduct(result);

                        // Attach fields from the CartLine
                        productInfo.productPrice = cartLine.Price ? cartLine.Price.toString() : '';
                        productInfo.productQuantity = cartLine.Quantity ? cartLine.Quantity.toString() : '';

                        return productInfo;
                    },
                    reason => {
                        throw new Error(`Failed to get product details for product ${cartLine.ProductId}.\n${reason}`);
                    }
                )
            );
        }
    }

    // Return promise for array of product results
    return Promise.all(productPromises);
};

/**
 * Converts a SimpleProduct object into an IProductInfo object
 * @param simpleProduct Simple Product object
 * @returns Populated IProductInfo
 */
export const getProductInfoFromSimpleProduct = (simpleProduct: SimpleProduct): IProductInfo => {
    const recordId = simpleProduct.RecordId || '';

    return {
        // If master product id is not available then product record id is master product id
        productId: simpleProduct.MasterProductId ? simpleProduct.MasterProductId.toString() : recordId.toString(),
        productSku: recordId ? recordId.toString() : '',
        productName: simpleProduct.Name || simpleProduct.Description || '',
        productPrice: simpleProduct.Price ? simpleProduct.Price.toString() : '',
        productChannelId: '', // Not needed for each product, sent as part of all events
        productChannelName: '',
        productCategoryId: '',
        productCategoryName: '',
        productQuantity: '', // Cannot be deteremined from SimpleProduct, will be added from CartLine later
        productCurrency: '' // Not needed for each product, sent as part of all events with product data
    };
};
